import React, { useState } from "react";
import { MPSharedTable, MPSharedDeleteModal, MPSharedHeader } from "../Shared";
import { useSearchQuery, usePagination, useDelete, useQuery } from "../../../hooks";
import { useGetVehicleGroupsQuery, useDeleteVehicleGroupMutation } from "../services";
import { VehicleGroupForm } from "./form";
import { VehicleGroupCsvUploadForm } from "./csvUploadForm";
import { Link } from "react-router-dom";
import { getLocalizedString } from "../../../shared/translation";

export const VehicleGroupsContainer = () => {
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToEditOrCsvUpload, setIdToEditOrCsvUpload] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [showCsvUploadForm, setShowCsvUploadForm] = useState(false);

  const { page, setPage, perPage, pagination } = usePagination();

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  const queryParams = {
    page: page,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
  };

  const query = useQuery();
  let filterHeader = "";
  const filters = [
    {
      label:
        " " +
        getLocalizedString("event_configuration_plan_lowercase", " event configuration plan") +
        ":",
      key: "event_handling_plan_id",
    },
  ];

  filters.map((filter) => {
    if (query.get(filter.key)) {
      queryParams[filter.key] = query.get(filter.key);
      filterHeader = `${filter.label} ${query.get("name")}`;
    }
  });

  const {
    data = {
      total_count: 0,
      data: [],
    },
    error,
    isFetching,
    isSuccess,
  } = useGetVehicleGroupsQuery({
    ...queryParams,
    q,
  });

  const {
    deleteItem: deleteVehicleGroup,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteVehicleGroupMutation,
    closeModal: () => setShowDeleteWarning(false),
  });

  const headers = [
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("vehicles", "Vehicles"),
      nestedValue: true,
      className: "vehicles text-center",
      getNestedValue: ({ resources_count, id, name }) => {
        return (
          <>
            {resources_count > 0 ? (
              <Link
                to={`/management_portal/vehicles/list?vehicle_group_id=${id}&name=${name}`}
                style={{ textDecoration: "none" }}
              >
                {resources_count}
              </Link>
            ) : (
              <span>0</span>
            )}
          </>
        );
      },
    },
    {
      label: getLocalizedString("added_on", "Added On"),
      key: "created_at",
      className: "date",
    },
    {
      label: getLocalizedString("added_by", "Added By"),
      key: "created_by",
      className: "added-by",
    },
  ];

  const onEdit = (data) => {
    setShowForm(true);
    setIdToEditOrCsvUpload(data.id);
  };

  const onUploadCsv = (data) => {
    setShowCsvUploadModal(true);
    setIdToEditOrCsvUpload(data.id);
  };

  const onDelete = (data) => {
    setShowDeleteWarning(true);
    setIdToDelete(data.id);
  };

  const onCreate = () => {
    setShowForm(true);
    setIdToEditOrCsvUpload(null);
  };

  const uploadCsvHandler = () => {
    setShowCsvUploadForm(true);
  };

  const getDeleteItemName = (id) => {
    let name = "";
    const index = data?.data.findIndex((item) => item.id === id);

    if (index >= 0) {
      name = data.data[index].name;
    }
    return name;
  };

  // Function to check if the group is deletable
  // A group is not deletable if it is a system group (by_system is true)
  const isDeletable = (data, id) => {
    const deletable = data?.data.some((item) => {
      if (item.id === id) return item.created_by === "System" ? false : true;
    });
    return deletable;
  };

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          groupName="group"
          resourceName="Groups"
          createNewHandler={onCreate}
          heading={getLocalizedString("groups", "Groups")}
          filterText={
            filterHeader && `${getLocalizedString("filtered_by", "Filtered by")} ${filterHeader}`
          }
          uploadCsvHandler={uploadCsvHandler}
          handleSearchKey={setSimpleSearch}
          simpleSearchPlaceholder={getLocalizedString("search_by_name", "Search by Name")}
        />

        <MPSharedTable
          resourceName="Groups"
          data={data?.data || []}
          error={error}
          isLoading={isFetching}
          isSuccess={isSuccess}
          headers={headers}
          onEdit={onEdit}
          onDelete={onDelete}
          onUploadCsv={onUploadCsv}
          pagination={{ ...pagination, count: data.total_count }}
          skipClientId
          className="vehicle-groups"
          auditResource="VehicleGroup"
        />
      </article>
      {/*  CSV Upload Form */}
      {showCsvUploadForm && (
        <VehicleGroupCsvUploadForm onClose={() => setShowCsvUploadForm(false)} />
      )}
      {/*  CSV Upload Form for a particular group*/}
      {showCsvUploadModal && (
        <VehicleGroupCsvUploadForm
          idToEditOrCsvUpload={idToEditOrCsvUpload}
          onClose={() => setShowCsvUploadModal(false)}
        />
      )}

      {showForm && (
        <VehicleGroupForm
          idToEditOrCsvUpload={idToEditOrCsvUpload}
          onClose={() => setShowForm(false)}
        />
      )}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName={getLocalizedString("vehicle_group", "Vehicle Group")}
        error={isDeletable(data, idToDelete) ? deleteErrorMsg : "Cannot delete system group"}
        itemName={getDeleteItemName(idToDelete)}
        onHide={() => {
          setDeleteErrorMsg("");
          setShowDeleteWarning(false);
        }}
        onDelete={() => deleteVehicleGroup({ id: idToDelete })}
      />
    </>
  );
};
